import { Injectable, OnDestroy } from '@angular/core';
import {
  CaasChosenNews,
  CaasComponents,
  CaasMainSection,
  TranslatedComponents,
} from '../shared/interfaces/caas/caas.interface';
import { AccordionItem } from '../shared/interfaces/accordion-item.interface';
import { UtilitiesCards } from '../shared/interfaces/utilities-cards.interface';
import { Teaser } from '../shared/interfaces/teaser.interface';
import { Observable, Subject, takeUntil, map, BehaviorSubject, of } from 'rxjs';
import { HrefType } from '../shared/enums/href-type.enums';
import { TeaserType } from '../shared/interfaces/teaser.interface';
import { News, NewsFilter } from '../shared/interfaces/news.interface';
import { ImagesService } from './images.service';
@Injectable({
  providedIn: 'root',
})
export class ManipulateDataService implements OnDestroy {
  private destroy = new Subject<void>();
  news: BehaviorSubject<News[]> = new BehaviorSubject<News[]>([]);
  newsFilters: BehaviorSubject<NewsFilter[]> = new BehaviorSubject<
    NewsFilter[]
  >([]);

  constructor(private imagesService: ImagesService) {}

  // Translate the components in mainSection to desired format
  translateComponents(data: CaasMainSection): TranslatedComponents {
    let hasWarned = false;
    const result: TranslatedComponents = {
      news: [],
      newsFilters: [],
      texts: [],
      images: [],
      highlights: [],
      termines: [],
      helpdisruptors: [],
      teasers: [],
      prefooters: [],
      downloads: [],
      deeplinks: [],
      trustedAwards: [],
      contacts: [],
      podcasts: [],
      podcastPlatforms: [],
      explanations: [],
      linktiles: [],
      events: [],
      accordeons: [],
      quotes: [],
      sliderQuotes: [],
    };

    const components = data?.formData?.st_items?.value;
    // Assign sequential IDs and distribute components into respective categories
    components?.forEach(
      (item: CaasComponents['value'][number], index: number) => {
        if (item.formData) {
          item.formData.id = index + 1;
        }

        const uids = Array.isArray(item.template.uid)
          ? item.template.uid
          : [item.template.uid];
        uids.forEach((uid) => {
          let isKnown = true;
          switch (uid) {
            case 'text':
            case 'st_text':
              result.texts.push(item);
              break;
            case 'st_image':
            case 'bild_grafik':
              result.images.push(item);
              break;
            case 'st_highlights':
              result.highlights.push(item);
              break;
            case 'st_termine':
              result.termines.push(item);
              break;
            case 'st_news':
              result.news.push(item);
              break;
            case 'st_helpdisruptor':
              result.helpdisruptors.push(item);
              break;
            case 'st_teasers':
              result.teasers.push(item);
              break;
            case 'st_teasers_temp':
              result.teasers.push(item);
              break;
            case 'st_downloads':
              result.downloads.push(item);
              break;
            case 'st_deeplink':
              result.deeplinks.push(item);
              break;
            case 'st_awards':
            case 'st_trusted_awards':
              result.trustedAwards.push(item);
              break;
            case 'contact':
            case 'st_contact':
              result.contacts.push(item);
              break;
            case 'st_podcasts':
              result.podcasts.push(item);
              break;
            case 'st_podcast_platforms':
              result.podcastPlatforms.push(item);
              break;
            case 'erklaerung':
            case 'st_info':
            case 'st_infoteasers':
              result.explanations.push(item);
              break;
            case 'st_linktiles':
              result.linktiles.push(item);
              break;
            case 'st_prefooter':
            case 'ueberschrift_media':
              result.prefooters.push(item);
              break;
            case 'st_events':
              result.events.push(item);
              break;
            case 'accordion':
            case 'st_accordion':
              result.accordeons.push(item);
              break;
            case 'st_quotes':
              if ((item.formData?.st_items?.value?.length || 0) > 1)
                result.sliderQuotes.push(item);
              if (item.formData?.st_items?.value?.length === 1)
                result.quotes.push(item);
              break;
            default:
              isKnown = false;
              break;
          }
          if (!isKnown && !hasWarned) {
            console.warn(`translateComponents > Unhandled UID: ${uid}`);
            hasWarned = true;
          }
        });
      }
    );

    // Sort components in each category by their assigned ID
    for (const key in result) {
      if (
        Object.hasOwn(result, key) &&
        Array.isArray(result[key as keyof TranslatedComponents])
      ) {
        result[key as keyof TranslatedComponents].sort(
          (a, b) => (a.formData?.id || 0) - (b.formData?.id || 0)
        );
      }
    }

    return result;
  }

  // It makes the News available for the components library
  setNews(news: News[]) {
    const sortedNews = news.sort((a, b) => b.timestamp - a.timestamp);
    this.news.next(sortedNews);
  }

  // It makes the NewsFilters available for the components library
  setNewsFilters(newsFilters: NewsFilter[]) {
    this.newsFilters.next(newsFilters);
  }

  /**
   * Fetches the news items by filter identifier.
   * It happens when the firstspirit user chose the automatic selection type
   * @param filterId The identifier of the filter.
   * @param quantity The number of news items to fetch.
   * @returns An observable of News array ordered by timestamp.
   */
  getNewsByFilterId(filterId: string, quantity: string): Observable<News[]> {
    const quantityNews = quantity ? parseInt(quantity, 10) : 3;

    if (filterId === 'latest' || !filterId) {
      // Fetch the latest news without any filter
      const sortedNews = [...this.news.value].sort(
        (a, b) => b.timestamp - a.timestamp
      );
      const slicedNews = sortedNews.slice(0, quantityNews);
      return of(slicedNews);
    }

    const selectedFilter = this.newsFilters.value.find(
      (filter) => filter.identifier === filterId
    );

    if (!selectedFilter) {
      console.warn(
        `ManipulateDataService > getNewsByFilterId - No filter found with identifier: ${filterId}`
      );
      return of([]);
    }

    const availableNews = this.news.value.filter((newsItem) =>
      newsItem.badges.some((badge) =>
        selectedFilter.categories.includes(badge.label)
      )
    );

    const sortedNews = availableNews.sort((a, b) => b.timestamp - a.timestamp);
    const slicedNews = sortedNews.slice(0, quantityNews);
    return of(slicedNews);
  }

  /**
   * Fetches the news items by choice.
   * It happens when the firstspirit user chose the manual selection type
   * @param news The news items to be chosen.
   * @returns An observable of News array
   */
  getNewsByChoice(news: CaasComponents['value']): Observable<News[]> {
    if (
      news.length &&
      (news[0] as unknown as CaasChosenNews).value?.target?.identifier &&
      this.news.value.length &&
      this.news.value[0].identifier
    ) {
      const newsIdentifiers = news.map(
        (chosenNews) =>
          (chosenNews as unknown as CaasChosenNews).value.target.identifier
      );
      const chosenNews = this.news.value.filter((newsItem) =>
        newsIdentifiers.some((identifier) => identifier === newsItem.identifier)
      );
      return of(chosenNews);
    }
    return of([]);
  }

  // Get accordion items from the main section
  getAccordionItems(mainSection: CaasMainSection): AccordionItem[] {
    const identifier = mainSection?.formData.st_typeOption?.value?.identifier;
    if (identifier === 'icon') {
      return mainSection.formData.st_icon_items!.value.map((item) => ({
        label: item.formData.st_itemHeadline.value,
        name: item.formData.st_itemHeadline.value,
        icon: (item.formData.st_icon.value as string) || '',
        Headline: '',
        key: item.identifier,
      }));
    } else {
      return [];
    }
  }

  // Get utilities cards list from the main section
  getUtilitiesCardsList(mainSection: CaasMainSection): UtilitiesCards[] {
    let utilitiesCardsList: UtilitiesCards[] = [];

    mainSection?.formData?.st_icon_items?.value.forEach((item) => {
      const hasTextList = item.formData.st_itemContent.value.some(
        (content) => content.template.uid === 'text_list'
      );
      if (hasTextList) {
        const textList = item.formData.st_itemContent.value
          .map((content) => {
            if (content.template.uid === 'text_list') {
              return {
                title: content.formData!.st_headline_text!.value,
                key: item.identifier,
                content: content.formData,
                items: content
                  .formData!.st_content!.value.split(/<p>|<\/p>/)
                  .filter((item) => item.trim() !== '')
                  .map((label) => {
                    // Extract the last word in all caps as badgeLabel
                    const match = label.match(/([A-Z]+)$/);
                    const badgeLabel = match ? match[1] : '';
                    return {
                      label: badgeLabel
                        ? label.replace(badgeLabel, '').trim()
                        : label.trim(),
                      badgeLabel: badgeLabel,
                    };
                  }),
              };
            } else {
              return undefined;
            }
          })
          .filter((card) => card !== undefined)
          .map((card) => {
            if (card) {
              return {
                title: card.title,
                key: card.key,
                items: Array.isArray(card.items)
                  ? card.items.map((item) => ({
                      label: item.label,
                      badgeLabel: item.badgeLabel,
                      rightIcon: (card.content?.st_icon?.value as string) || '',
                    }))
                  : [],
              };
            }
            return undefined;
          });
        utilitiesCardsList = utilitiesCardsList.concat(
          textList.filter(Boolean) as UtilitiesCards[]
        );
      }
    });

    return utilitiesCardsList;
  }

  // Check if Material Download section is included in the mainSection
  checkMaterialDownloadInclude(mainSection: CaasMainSection): boolean {
    const hasMaterialDownload =
      mainSection?.formData.st_items?.value.some(
        (component) => component.template.uid === 'st_downloads'
      ) || false;
    const hasDeepLink =
      mainSection?.formData.st_items?.value.some(
        (component) => component.template.uid === 'st_deeplink'
      ) || false;
    return hasMaterialDownload && hasDeepLink;
  }

  // Get the Material Download deep links if available
  getMaterialDownloadDeepLinks(
    mainSection: CaasMainSection
  ): CaasComponents['value'] | undefined {
    return mainSection?.formData.st_items?.value.filter(
      (component) => component.template.uid === 'st_deeplink'
    );
  }

  getTeasersNewsByFilterId(
    filterId: string,
    quantity: string
  ): Observable<Teaser[]> {
    return this.getNewsByFilterId(filterId, quantity).pipe(
      takeUntil(this.destroy),
      map((filteredNews: News[]) => {
        return filteredNews.map((newsItem: News) => {
          // Extract link information from rawLink
          const rawLink = newsItem.rawLink;
          let href = '';
          let hrefType: HrefType = HrefType.ANGULAR_ROUTE;

          if (rawLink?.value?.formData?.lt_link_internal?.value) {
            // Internal link exists
            href = rawLink.value.formData.lt_link_internal.value.url;
            hrefType = HrefType.ANGULAR_ROUTE;
          } else if (rawLink?.value?.formData?.lt_link_external?.value) {
            // External link exists
            href = rawLink.value.formData.lt_link_external.value;
            hrefType = HrefType.EXTERNAL;
          }

          const teaser: Teaser = {
            imageBorder: true,
            badges: newsItem.badges,
            type: TeaserType.SMALL,
            headline: newsItem.title,
            description: newsItem.description,
            imageSrc: newsItem.imageSrc,
            imageAlt:
              this.imagesService.getAltTextById(newsItem.imageIdentifier) ?? '',
            imageTitle: newsItem.imageTitle,
            label:
              rawLink?.value?.formData?.lt_link_label?.value ?? 'Mehr erfahren',
            hrefType: hrefType,
            href: href,
            title: rawLink?.value?.formData?.lt_link_title?.value ?? '',
            fragment:
              rawLink?.value?.formData?.st_anchor_name?.value ?? undefined,
          };
          return teaser;
        });
      })
    );
  }

  // Similarly, update getTeasersNewsByChoice
  getTeasersNewsByChoice(news: CaasComponents['value']): Observable<Teaser[]> {
    return this.getNewsByChoice(news).pipe(
      takeUntil(this.destroy),
      map((filteredNews: News[]) => {
        return filteredNews.map((newsItem: News) => {
          // Extract link information from rawLink
          const rawLink = newsItem.rawLink;
          let href = '';
          let hrefType: HrefType = HrefType.ANGULAR_ROUTE;

          if (rawLink?.value?.formData?.lt_link_internal?.value) {
            // Internal link exists
            href = rawLink.value.formData.lt_link_internal.value.url;
            hrefType = HrefType.ANGULAR_ROUTE;
          } else if (rawLink?.value?.formData?.lt_link_external?.value) {
            // External link exists
            href = rawLink.value.formData.lt_link_external.value;
            hrefType = HrefType.EXTERNAL;
          }

          const teaser: Teaser = {
            imageBorder: true,
            badges: newsItem.badges,
            type: TeaserType.SMALL,
            headline: newsItem.title,
            description: newsItem.description,
            imageSrc: newsItem.imageSrc,
            imageAlt:
              this.imagesService.getAltTextById(newsItem.imageIdentifier) ?? '',
            imageTitle: newsItem.imageTitle,
            label:
              rawLink?.value?.formData?.lt_link_label?.value ?? 'Mehr erfahren',
            hrefType: hrefType,
            href: href,
            title: rawLink?.value?.formData?.lt_link_title?.value ?? '',
            fragment:
              rawLink?.value?.formData?.st_anchor_name?.value ?? undefined,
          };
          return teaser;
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
